import {ReactNode} from "react";
import {cva, type VariantProps} from "class-variance-authority";
import {X} from "lucide-react";
import {useTranslationWithKeys} from "../../../hooks/useTranslationWithKeys";
import {Button} from "../../basic/button";
import {cn} from "../../../utils/utils";

/**
 * Banners vary along two independent axes:
 * - `variant` is the tone. Apart from `promo` every tone sits on the white card
 *   surface and carries its colour in the border, the icon and the action
 *   button, so banners stay quiet until their accent says otherwise.
 * - `prominence` is how much room it claims. `default` is a card for the
 *   floating {@link BannerStack}; `hero` is a full-bleed band meant for the
 *   in-flow {@link HeroBannerSlot}, where it pushes the page down instead of
 *   overlaying it.
 */
const bannerVariants = cva(
    "relative overflow-hidden",
    {
        variants: {
            variant: {
                neutral: "bg-card border-primary/40 text-card-foreground",
                warning: "bg-card border-warning text-card-foreground",
                destructive: "bg-card border-destructive text-card-foreground",
                promo: "bg-gradient-to-br from-info via-info via-80% to-primary border-transparent text-info-foreground",
            },
            prominence: {
                default: "rounded-lg border-2 p-6 shadow-xl",
                hero: "border-b-2 shadow-lg",
            },
        },
        defaultVariants: {
            variant: "neutral",
            prominence: "default",
        },
    },
);

const contentVariants = cva(
    "flex w-full items-start justify-between gap-4",
    {
        variants: {
            prominence: {
                default: "text-lg",
                hero: "mx-auto max-w-(--content-max-width) px-6 py-8 lg:gap-8 lg:px-12 lg:py-12",
            },
        },
        defaultVariants: {prominence: "default"},
    },
);

const iconVariants = cva(
    "flex-shrink-0",
    {
        variants: {
            variant: {
                neutral: "bg-accent text-primary",
                warning: "bg-warning/10 text-warning",
                destructive: "bg-destructive/10 text-destructive",
                promo: "bg-white/20",
            },
            prominence: {
                default: "rounded-full p-2 [&>svg]:h-6 [&>svg]:w-6",
                hero: "hidden rounded-full p-3 sm:block lg:p-4 [&>svg]:h-8 [&>svg]:w-8 lg:[&>svg]:h-12 lg:[&>svg]:w-12",
            },
        },
        defaultVariants: {
            variant: "neutral",
            prominence: "default",
        },
    },
);

const messageVariants = cva(
    "",
    {
        variants: {
            prominence: {
                default: "font-medium",
                hero: "text-2xl font-bold tracking-tight lg:text-4xl",
            },
        },
        defaultVariants: {prominence: "default"},
    },
);

const descriptionVariants = cva(
    "",
    {
        variants: {
            variant: {
                neutral: "text-muted-foreground",
                warning: "text-muted-foreground",
                destructive: "text-muted-foreground",
                promo: "opacity-95",
            },
            prominence: {
                default: "mt-1 text-base",
                hero: "mt-2 max-w-3xl text-base lg:mt-3 lg:text-xl",
            },
        },
        defaultVariants: {
            variant: "neutral",
            prominence: "default",
        },
    },
);

const actionVariants = cva(
    "font-medium",
    {
        variants: {
            variant: {
                neutral: "bg-primary text-primary-foreground hover:bg-primary/90",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                promo: "bg-card text-foreground hover:bg-card/90",
            },
            prominence: {
                default: "mt-3",
                hero: "mt-5 font-semibold lg:mt-8 lg:h-14 lg:px-8 lg:text-lg",
            },
        },
        defaultVariants: {
            variant: "neutral",
            prominence: "default",
        },
    },
);

const closeVariants = cva(
    "flex-shrink-0 rounded p-1 transition-opacity hover:opacity-70",
    {
        variants: {
            variant: {
                neutral: "text-muted-foreground hover:bg-accent",
                warning: "text-muted-foreground hover:bg-warning/10",
                destructive: "text-muted-foreground hover:bg-destructive/10",
                promo: "text-current hover:bg-white/20",
            },
        },
        defaultVariants: {variant: "neutral"},
    },
);

interface NotificationBannerProps extends VariantProps<typeof bannerVariants> {
    message: ReactNode;
    /** Secondary line below the message. Mostly used at `hero` prominence. */
    description?: ReactNode;
    icon?: ReactNode;
    /** Decorative background layer, e.g. an oversized watermark icon. */
    decoration?: ReactNode;
    /** Content shown to the right of the text, or below it on small screens. */
    aside?: ReactNode;
    action?: {
        label: ReactNode;
        onClick: () => void;
    };
    onClose?: () => void;
    className?: string;
}

export function NotificationBanner({
                                       variant,
                                       prominence,
                                       message,
                                       description,
                                       icon,
                                       decoration,
                                       aside,
                                       action,
                                       onClose,
                                       className,
                                   }: NotificationBannerProps) {
    const {t} = useTranslationWithKeys();
    const isHero = prominence === "hero";

    return (
        <div
            className={cn(bannerVariants({variant, prominence}), className)}
            role={isHero ? "region" : "alert"}
            aria-label={isHero && typeof message === "string" ? message : undefined}
        >
            {decoration}
            <div className={cn(contentVariants({prominence}))}>
                <div className={cn("flex flex-1 items-start", isHero ? "gap-4 lg:gap-8" : "gap-3")}>
                    {icon && <span className={cn(iconVariants({variant, prominence}))}>{icon}</span>}
                    <div className="flex flex-1 flex-col gap-6 lg:flex-row lg:items-center lg:gap-12">
                        <div className="flex-1">
                            {isHero ? (
                                <h2 className={cn(messageVariants({prominence}))}>{message}</h2>
                            ) : (
                                <p className={cn(messageVariants({prominence}))}>{message}</p>
                            )}
                            {description && <p className={cn(descriptionVariants({variant, prominence}))}>{description}</p>}
                            {action && (
                                <Button
                                    size={isHero ? "lg" : "sm"}
                                    className={cn(actionVariants({variant, prominence}))}
                                    onClick={action.onClick}
                                >
                                    {action.label}
                                </Button>
                            )}
                        </div>
                        {aside && <div className="lg:shrink-0">{aside}</div>}
                    </div>
                </div>
                {onClose && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className={cn(closeVariants({variant}))}
                        aria-label={t('common.close')}
                    >
                        <X className="h-6 w-6"/>
                    </Button>
                )}
            </div>
        </div>
    );
}
